<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Payout Ops</span>
          <h2 class="workspace-title">Settlements, buckets y órdenes manuales</h2>
          <p class="workspace-copy">Aquí puedes ver cuánto está liberable, crear órdenes y marcar payouts manuales como pagados sin pasarte del saldo.</p>
        </div>
      </div>

      <div class="admin-filters">
        <input v-model="eventIdFilter" class="workspace-input" type="number" min="1" placeholder="Event ID" />
        <input v-model="organizerIdFilter" class="workspace-input" type="number" min="1" placeholder="Organizer ID" />
        <select v-model="statusFilter" class="workspace-input workspace-select">
          <option value="">Todos los estados</option>
          <option value="held">Held</option>
          <option value="blocked">Blocked</option>
          <option value="releasable">Releasable</option>
          <option value="partially_paid">Partially paid</option>
          <option value="paid">Paid</option>
        </select>
        <button class="workspace-button workspace-button--primary" type="button" :disabled="loading" @click="loadData">Actualizar</button>
      </div>

      <div v-if="feedback" :class="['workspace-feedback', feedbackType === 'error' ? 'workspace-feedback--error' : 'workspace-feedback--success']">{{ feedback }}</div>

      <div v-if="loading" class="workspace-empty">Cargando settlements y payouts...</div>
      <div v-else class="admin-report-grid payout-grid">
        <article class="admin-panel-card payout-panel">
          <div class="workspace-panel__header"><div><span class="workspace-kicker">Settlements</span><h3 class="workspace-title">Fuente para nuevas ordenes</h3></div></div>
          <div v-if="!settlements.length" class="workspace-empty">No hay settlements para ese filtro.</div>
          <div v-else class="payout-settlement-list">
            <button v-for="settlement in settlements" :key="settlement.id" :class="['payout-settlement-card', { 'is-active': selectedSettlement && selectedSettlement.id === settlement.id }]" type="button" @click="selectSettlement(settlement)">
              <div class="workspace-badge-row workspace-badge-row--compact">
                <span class="workspace-badge">#{{ settlement.id }}</span>
                <span class="workspace-badge">{{ settlement.currency }}</span>
                <span :class="['workspace-status-pill', settlement.status === 'releasable' ? 'is-active' : 'is-warning']">{{ settlement.status }}</span>
              </div>
              <strong>Event {{ settlement.event }}</strong>
              <small>Organizer {{ settlement.organizer }}</small>
              <div class="payout-mini-grid">
                <span>Liberable: ${{ money(settlement.available_to_order) }}</span>
                <span>Pagado: ${{ money(settlement.paid_amount) }}</span>
              </div>
            </button>
          </div>
        </article>

        <article class="admin-panel-card payout-panel">
          <div class="workspace-panel__header"><div><span class="workspace-kicker">Create Order</span><h3 class="workspace-title">Draft manual o API</h3></div></div>
          <div v-if="!selectedSettlement" class="workspace-empty">Selecciona un settlement para crear una orden.</div>
          <template v-else>
            <div class="payout-summary-grid">
              <article class="admin-kpi"><small>Total liberable</small><strong>${{ money(selectedSettlement.available_to_order) }}</strong></article>
              <article class="admin-kpi"><small>Pre-evento</small><strong>${{ money(selectedSettlement.releasable_pre_event) }}</strong></article>
              <article class="admin-kpi"><small>Post-evento</small><strong>${{ money(selectedSettlement.releasable_post_event) }}</strong></article>
            </div>
            <div class="workspace-form workspace-form--two">
              <label class="workspace-field">
                <span>Funding bucket</span>
                <select v-model="draft.funding_bucket_id" class="workspace-input workspace-select">
                  <option value="">Sin bucket especifico</option>
                  <option v-for="bucket in selectedSettlement.funding_buckets || []" :key="bucket.id" :value="bucket.id">{{ bucket.source_provider }} - {{ bucket.currency }} - ${{ money(bucket.available_to_order) }}</option>
                </select>
              </label>
              <label class="workspace-field">
                <span>Rail</span>
                <select v-model="draft.rail" class="workspace-input workspace-select">
                  <option value="manual_bank_transfer">Manual bank transfer</option>
                  <option value="manual_external_provider">Manual external provider</option>
                  <option value="stripe_global_payouts_api">Stripe Global Payouts API</option>
                </select>
              </label>
              <label class="workspace-field">
                <span>Funding source</span>
                <select v-model="draft.funding_source" class="workspace-input workspace-select">
                  <option value="stripe">Stripe</option>
                  <option value="paypal">PayPal</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label class="workspace-field">
                <span>Monto</span>
                <input v-model="draft.amount" class="workspace-input" type="number" min="0" step="0.01" />
              </label>
              <label class="workspace-field workspace-field--wide">
                <span>Comentario</span>
                <input v-model="draft.comment" class="workspace-input" type="text" placeholder="Referencia interna o nota de tesoreria" />
              </label>
            </div>
            <div class="workspace-actions workspace-actions--end">
              <button class="workspace-button workspace-button--primary" type="button" :disabled="saving" @click="createOrder">{{ saving ? 'Creando...' : 'Crear draft' }}</button>
            </div>
          </template>
        </article>
      </div>
    </article>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Orders</span>
          <h2 class="workspace-title">Ciclo de vida manual</h2>
          <p class="workspace-copy">Aprueba, envia, marca paid o libera reserva en fallos/cancelaciones.</p>
        </div>
      </div>

      <div v-if="!orders.length" class="workspace-empty">Aún no hay payout orders.</div>
      <div v-else class="workspace-table-shell">
        <table class="workspace-table">
          <thead><tr><th>ID</th><th>Organizer</th><th>Rail</th><th>Source</th><th>Monto</th><th>Estado</th><th>Referencia</th><th></th></tr></thead>
          <tbody>
            <tr v-for="order in orders" :key="order.id">
              <td>#{{ order.id }}</td>
              <td>{{ order.organizer }}</td>
              <td>{{ order.rail }}</td>
              <td>{{ order.funding_source }}</td>
              <td>${{ money(order.amount) }}</td>
              <td><span :class="['workspace-status-pill', order.status === 'paid' ? 'is-active' : order.status === 'failed' || order.status === 'cancelled' ? 'is-warning' : '']">{{ order.status }}</span></td>
              <td>{{ order.external_reference || order.provider_reference || '-' }}</td>
              <td>
                <div class="workspace-actions workspace-actions--compact payout-actions">
                  <button v-if="order.status === 'draft'" class="workspace-button workspace-button--small workspace-button--primary" type="button" @click="transition(order, 'approve')">Approve</button>
                  <button v-if="order.status === 'approved'" class="workspace-button workspace-button--small" type="button" @click="transition(order, 'submit')">Submit</button>
                  <button v-if="['approved', 'submitted', 'processing'].includes(order.status)" class="workspace-button workspace-button--small" type="button" @click="markPaid(order)">Mark paid</button>
                  <button v-if="['approved', 'submitted', 'processing'].includes(order.status)" class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="transition(order, 'fail')">Fail</button>
                  <button v-if="order.status !== 'paid' && order.status !== 'cancelled'" class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="transition(order, 'cancel')">Cancel</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'

export default {
  name: 'PayoutOpsView',
  data() {
    return {
      settlements: [],
      orders: [],
      loading: false,
      saving: false,
      eventIdFilter: '',
      organizerIdFilter: '',
      statusFilter: '',
      selectedSettlement: null,
      feedback: '',
      feedbackType: 'success',
      draft: { funding_bucket_id: '', rail: 'manual_bank_transfer', funding_source: 'stripe', amount: '', comment: '' },
    }
  },
  created() { this.loadData() },
  methods: {
    extractError(payload, fallback) {
      if (!payload) return fallback
      if (typeof payload === 'string') return payload
      if (payload.err) return payload.err
      if (payload.detail) return Array.isArray(payload.detail) ? payload.detail.join(', ') : payload.detail
      return fallback
    },
    money(value) { const parsed = Number(value || 0); return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00' },
    async loadData() {
      this.loading = true
      this.feedback = ''
      const query = new URLSearchParams()
      if (this.eventIdFilter) query.set('event_id', this.eventIdFilter)
      if (this.organizerIdFilter) query.set('organizer_id', this.organizerIdFilter)
      if (this.statusFilter) query.set('status', this.statusFilter)
      const suffix = query.toString() ? `?${query.toString()}` : ''
      const [settlements, orders] = await Promise.all([apiService(`/api/admin/payouts/settlements/${suffix}`), apiService('/api/admin/payouts/orders/')])
      this.loading = false
      if (Array.isArray(settlements)) this.settlements = settlements
      else this.settlements = []
      if (Array.isArray(orders)) this.orders = orders
      else this.orders = []
      if (!Array.isArray(settlements)) { this.feedback = this.extractError(settlements, 'No pudimos cargar settlements.'); this.feedbackType = 'error' }
      else if (!Array.isArray(orders)) { this.feedback = this.extractError(orders, 'No pudimos cargar payout orders.'); this.feedbackType = 'error' }
    },
    selectSettlement(settlement) {
      this.selectedSettlement = settlement
      const firstBucket = (settlement.funding_buckets || [])[0]
      this.draft.funding_bucket_id = firstBucket ? String(firstBucket.id) : ''
      this.draft.funding_source = firstBucket ? firstBucket.source_provider : 'stripe'
      this.draft.amount = settlement.available_to_order || ''
    },
    async createOrder() {
      if (!this.selectedSettlement) return
      this.saving = true
      const payload = { settlement_id: this.selectedSettlement.id, funding_bucket_id: this.draft.funding_bucket_id || null, rail: this.draft.rail, funding_source: this.draft.funding_source, amount: this.draft.amount, comment: this.draft.comment }
      const data = await apiService('/api/admin/payouts/orders/', 'POST', payload)
      this.saving = false
      if (data && !data.err && !data.detail) { this.feedback = `Payout order #${data.id} creada.`; this.feedbackType = 'success'; await this.loadData(); return }
      this.feedback = this.extractError(data, 'No pudimos crear la orden.')
      this.feedbackType = 'error'
    },
    async transition(order, action) {
      const data = await apiService(`/api/admin/payouts/orders/${order.id}/${action}/`, 'POST', {})
      if (data && !data.err && !data.detail) { this.feedback = `Orden #${order.id} -> ${action}`; this.feedbackType = 'success'; await this.loadData(); return }
      this.feedback = this.extractError(data, `No pudimos ejecutar ${action}.`)
      this.feedbackType = 'error'
    },
    async markPaid(order) {
      const reference = window.prompt('Referencia externa o comprobante', order.external_reference || '')
      if (reference === null) return
      const data = await apiService(`/api/admin/payouts/orders/${order.id}/mark-paid/`, 'POST', { external_reference: reference })
      if (data && !data.err && !data.detail) { this.feedback = `Orden #${order.id} marcada como paid.`; this.feedbackType = 'success'; await this.loadData(); return }
      this.feedback = this.extractError(data, 'No pudimos marcarla como paid.')
      this.feedbackType = 'error'
    },
  },
}
</script>

<style scoped>
.payout-grid { display: grid; grid-template-columns: 1.2fr 1fr; gap: 20px; }
.payout-panel { min-height: 100%; }
.payout-settlement-list { display: grid; gap: 12px; max-height: 560px; overflow: auto; }
.payout-settlement-card { border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 16px; padding: 14px; background: rgba(255,255,255,0.9); text-align: left; display: grid; gap: 8px; }
.payout-settlement-card.is-active { border-color: #111827; box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12); }
.payout-mini-grid { display: grid; gap: 4px; color: #475569; font-size: 0.9rem; }
.payout-summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
.payout-actions { justify-content: flex-end; }
@media (max-width: 960px) { .payout-grid, .payout-summary-grid { grid-template-columns: 1fr; } }
</style>
